import React, { useRef, useState } from "react";
import {
  ArrowRight,
  Camera,
  CheckCircle2,
  ChevronDown,
  Loader2,
} from "lucide-react";
import { supabase } from "../lib/supabaseClient";

const initialForm = {
  name: "",
  phone: "",
  email: "",
  service: "Lawn Mowing & Maintenance",
  location: "",
  details: "",
  preferred_contact: "No preference",
};

function cleanFileName(name) {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9._-]/g, "-")
    .replace(/-+/g, "-");
}

export default function QuoteForm({ services }) {
  const [form, setForm] = useState(initialForm);
  const [files, setFiles] = useState([]);
  const [status, setStatus] = useState("idle");
  const [message, setMessage] = useState("");
  const fileInputRef = useRef(null);

  const isSubmitting = status === "submitting";

  function updateField(field, value) {
    setForm((current) => ({
      ...current,
      [field]: value,
    }));
  }

  async function trackEvent(eventType, metadata = {}) {
    try {
      await supabase.from("site_events").insert({
        event_type: eventType,
        page: window.location.pathname,
        metadata,
      });
    } catch {
      // Do not block the customer if metrics fail.
    }
  }

  async function notifyOwner(quoteRequestId) {
    try {
      const { error: notifyError } = await supabase.functions.invoke("notify-quote", {
        body: {
          quote_request_id: quoteRequestId,
        },
      });

      if (notifyError) {
        console.error("Quote notification failed:", notifyError);
      }
    } catch (notifyError) {
      console.error("Quote notification failed:", notifyError);
    }
  }

  async function handleSubmit(event) {
    event.preventDefault();

    if (isSubmitting) return;

    if (!form.name.trim() || !form.phone.trim() || !form.details.trim()) {
      setStatus("error");
      setMessage("Name, phone, and job details are required.");
      return;
    }

    setStatus("submitting");
    setMessage("");

    try {
      const quoteRequestId = crypto.randomUUID();

      const { error: quoteError } = await supabase.from("quote_requests").insert({
        id: quoteRequestId,
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim() || null,
        service: form.service,
        location: form.location.trim() || null,
        details: form.details.trim(),
        preferred_contact: form.preferred_contact,
        status: "New",
      });

      if (quoteError) throw quoteError;

      const uploadedPhotoRows = [];

      for (const file of files) {
        if (!file.type.startsWith("image/")) {
          throw new Error(`${file.name} is not an image file.`);
        }

        if (file.size > 8 * 1024 * 1024) {
          throw new Error(`${file.name} is larger than 8 MB.`);
        }

        const safeName = cleanFileName(file.name);
        const filePath = `${quoteRequestId}/${Date.now()}-${safeName}`;

        const { error: uploadError } = await supabase.storage
          .from("quote-photos")
          .upload(filePath, file, {
            cacheControl: "3600",
            upsert: false,
            contentType: file.type,
          });

        if (uploadError) throw uploadError;

        uploadedPhotoRows.push({
          quote_request_id: quoteRequestId,
          file_path: filePath,
          file_name: file.name,
          file_type: file.type,
          file_size: file.size,
        });
      }

      if (uploadedPhotoRows.length > 0) {
        const { error: photoError } = await supabase
          .from("quote_request_photos")
          .insert(uploadedPhotoRows);

        if (photoError) throw photoError;
      }

      await trackEvent("quote_submit", {
        quote_request_id: quoteRequestId,
        service: form.service,
        photo_count: files.length,
      });

      await notifyOwner(quoteRequestId);

      setStatus("success");
      setMessage("Quote request submitted. Precision Lawn Workx LLC will review it and follow up.");
      setForm(initialForm);
      setFiles([]);

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (error) {
      console.error("Quote submission failed:", error);
      setStatus("error");
      setMessage(error.message || "Something went wrong submitting the request.");
    }
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="grid gap-4 bg-white p-6 text-slate-950 sm:p-8 lg:p-10"
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <label className="grid gap-2 text-sm font-black">
          Name *
          <input
            value={form.name}
            onChange={(event) => updateField("name", event.target.value)}
            disabled={isSubmitting}
            className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4 font-semibold outline-none transition focus:border-green-500 focus:bg-white disabled:cursor-not-allowed disabled:opacity-70"
            placeholder="Your name"
          />
        </label>

        <label className="grid gap-2 text-sm font-black">
          Phone *
          <input
            value={form.phone}
            onChange={(event) => updateField("phone", event.target.value)}
            disabled={isSubmitting}
            className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4 font-semibold outline-none transition focus:border-green-500 focus:bg-white disabled:cursor-not-allowed disabled:opacity-70"
            placeholder="Best phone number"
          />
        </label>
      </div>

      <label className="grid gap-2 text-sm font-black">
        Email
        <input
          value={form.email}
          onChange={(event) => updateField("email", event.target.value)}
          disabled={isSubmitting}
          className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4 font-semibold outline-none transition focus:border-green-500 focus:bg-white disabled:cursor-not-allowed disabled:opacity-70"
          placeholder="Email address"
        />
      </label>

      <label className="grid gap-2 text-sm font-black">
        Service needed
        <div className="relative">
          <select
            value={form.service}
            onChange={(event) => updateField("service", event.target.value)}
            disabled={isSubmitting}
            className="w-full appearance-none rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4 font-semibold outline-none transition focus:border-green-500 focus:bg-white disabled:cursor-not-allowed disabled:opacity-70"
          >
            {services.map((service) => (
              <option key={service.id}>{service.label}</option>
            ))}
            <option>Not sure / multiple services</option>
          </select>

          <ChevronDown className="pointer-events-none absolute right-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
        </div>
      </label>

      <label className="grid gap-2 text-sm font-black">
        Preferred contact
        <div className="relative">
          <select
            value={form.preferred_contact}
            onChange={(event) => updateField("preferred_contact", event.target.value)}
            disabled={isSubmitting}
            className="w-full appearance-none rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4 font-semibold outline-none transition focus:border-green-500 focus:bg-white disabled:cursor-not-allowed disabled:opacity-70"
          >
            <option>No preference</option>
            <option>Call</option>
            <option>Text</option>
            <option>Email</option>
          </select>

          <ChevronDown className="pointer-events-none absolute right-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
        </div>
      </label>

      <label className="grid gap-2 text-sm font-black">
        Job location / area
        <input
          value={form.location}
          onChange={(event) => updateField("location", event.target.value)}
          disabled={isSubmitting}
          className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4 font-semibold outline-none transition focus:border-green-500 focus:bg-white disabled:cursor-not-allowed disabled:opacity-70"
          placeholder="City or address"
        />
      </label>

      <label className="grid gap-2 text-sm font-black">
        Job details *
        <textarea
          value={form.details}
          onChange={(event) => updateField("details", event.target.value)}
          disabled={isSubmitting}
          rows="5"
          className="resize-none rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4 font-semibold outline-none transition focus:border-green-500 focus:bg-white disabled:cursor-not-allowed disabled:opacity-70"
          placeholder="Describe what needs mowed, trimmed, cleaned, removed, or landscaped."
        />
      </label>

      <label
        className={`flex items-center justify-center gap-3 rounded-2xl border-2 border-dashed border-slate-300 bg-slate-50 px-4 py-5 text-sm font-black text-slate-500 transition ${
          isSubmitting
            ? "cursor-not-allowed opacity-70"
            : "cursor-pointer hover:border-green-300 hover:bg-lime-50 hover:text-green-700"
        }`}
      >
        <Camera className="h-5 w-5" />
        {files.length > 0 ? `${files.length} photo(s) selected` : "Attach job photos"}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*"
          disabled={isSubmitting}
          className="hidden"
          onChange={(event) => {
            const selectedFiles = Array.from(event.target.files || []);
            setFiles(selectedFiles);
          }}
        />
      </label>

      {files.length > 0 && (
        <div className="grid gap-2 rounded-2xl bg-slate-50 p-4 text-sm font-semibold text-slate-600 ring-1 ring-black/5">
          {files.map((file) => (
            <div key={`${file.name}-${file.size}`} className="flex items-center justify-between gap-3">
              <span className="truncate">{file.name}</span>
              <span className="shrink-0 text-xs text-slate-400">
                {(file.size / 1024 / 1024).toFixed(1)} MB
              </span>
            </div>
          ))}
        </div>
      )}

      <button
        type="submit"
        disabled={isSubmitting}
        className="group mt-2 inline-flex items-center justify-center gap-3 rounded-2xl bg-black px-6 py-4 font-black text-white shadow-xl shadow-slate-900/15 transition hover:-translate-y-0.5 hover:bg-green-800 disabled:cursor-not-allowed disabled:opacity-70"
      >
        {isSubmitting ? (
          <>
            <Loader2 className="h-5 w-5 animate-spin" />
            Submitting Request
          </>
        ) : (
          <>
            Submit Quote Request
            <ArrowRight className="h-5 w-5 transition group-hover:translate-x-1" />
          </>
        )}
      </button>

      {message && (
        <div
          className={`flex items-start gap-3 rounded-2xl p-4 text-sm font-bold ${
            status === "success"
              ? "bg-lime-50 text-green-800 ring-1 ring-lime-200"
              : "bg-red-50 text-red-700 ring-1 ring-red-200"
          }`}
        >
          {status === "success" && <CheckCircle2 className="h-5 w-5 shrink-0" />}
          <p>{message}</p>
        </div>
      )}
    </form>
  );
}